import { useState } from 'react';

import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { collectionApis } from '../../../apis/collectionApis';
import { FILE_SIZE, MAX_FILE_NUMBER } from '../../../constants';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import {
  fileTooBig,
  fileNotZip,
  formatBytes,
  sentenceCase,
} from '../../../utils/utils';
import Modal from '../../sections/modal/Modal';
import ModalButtons from '../../sections/modal/ModalButtons';
import { MIME_TYPE } from '../file-manager/constants';
import FileUpload from '../file-upload/FileUpload';

const ImportMenuItem = ({ name, onClose }) => {
  const { t } = useTranslation();
  const { projectId, instanceId } = useParams();
  const { showToast } = useToast();

  const [modal, setModal] = useState(false);
  const [files, setFiles] = useState([]);
  const [sizeError, setSizeError] = useState(false);
  const [formatError, setFormatError] = useState(false);

  const toggleModal = () => setModal(!modal);

  const handleImport = async (formData) => {
    try {
      const data = new FormData();
      const blob = new Blob([files[0]], { type: MIME_TYPE.zip });
      data.append('file', blob);
      await collectionApis.importCollection(
        projectId,
        instanceId,
        name,
        formData,
        data
      );

      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('importSuccess', {
            ns: 'notifications',
            text: t('collection', { ns: 'instance' }),
          })
        )
      );
    } catch {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('importError', {
            ns: 'notifications',
            text: t('collection', { ns: 'instance' }),
          })
        )
      );
    } finally {
      onClose();
      toggleModal();
    }
  };

  const handleCancel = () => {
    onClose();
    toggleModal();
    setFiles([]);
    setFormatError(false);
    setSizeError(false);
  };

  const { handleSubmit, control } = useForm();

  const onDrop = (acceptedFiles) => {
    if (isEmpty(acceptedFiles) || files.length >= MAX_FILE_NUMBER) return;

    setSizeError(fileTooBig(acceptedFiles[0].size, FILE_SIZE.BYTES));
    setFormatError(fileNotZip(acceptedFiles[0].type));

    if (
      !fileTooBig(acceptedFiles[0].size, FILE_SIZE.BYTES) &&
      !fileNotZip(acceptedFiles[0].type)
    ) {
      setSizeError(false);
      setFormatError(false);
      setFiles([...files, ...acceptedFiles]);
    }
  };

  const { getRootProps, open, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    noClick: true,
    noKeyboard: true,
  });

  const removeFile = (fileIndex) => {
    const newFiles = [...files];
    newFiles.splice(newFiles.indexOf(fileIndex), 1);
    setFiles(newFiles);
  };

  const checkboxInputs = [
    { name: 'clear', label: 'deleteConf' },
    { name: 'apply', label: 'reloadAfterImport' },
  ];

  return (
    <>
      <MenuItem onClick={toggleModal}>
        {t('import', { ns: 'buttons' })}
      </MenuItem>
      <Modal
        openModal={modal}
        title={t('collectionImport', { ns: 'instance', name: name })}
        handleClose={handleCancel}
        action={
          <ModalButtons
            onCancel={handleCancel}
            onAction={handleSubmit(handleImport)}
            cancelProps={{ type: 'reset' }}
            actionProps={{
              disabled: sizeError || formatError || isEmpty(files),
            }}
            actionText={t('confirm', { ns: 'buttons' })}
          />
        }
      >
        <Alert severity="warning" sx={{ mb: 1, mx: 1 }}>
          {t('collectionImportWarning', { ns: 'descriptions' })}
        </Alert>
        <form onSubmit={handleSubmit(handleImport)}>
          <div {...getRootProps({ className: 'dropzone' })}>
            <FileUpload
              files={files}
              open={open}
              handleRemove={removeFile}
              formatError={formatError}
              sizeError={sizeError}
              sizeLimit={formatBytes(FILE_SIZE.BYTES, 0)}
              helperText={t('dropOneZipFile', { ns: 'instance' })}
              fileErrorMsg={t('onlyZipFile', { ns: 'descriptions' })}
            />
            <input {...getInputProps()} />
          </div>
          {checkboxInputs.map((input) => (
            <Controller
              name={input.name}
              control={control}
              defaultValue={false}
              key={input.name}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} />}
                  label={t(input.label, { ns: 'instance' })}
                />
              )}
            />
          ))}
        </form>
      </Modal>
    </>
  );
};

ImportMenuItem.propTypes = {
  name: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ImportMenuItem;
